<template>
    <ModalBase v-if="visible" :with-content="true" @close="cancel">
        <h1>{{ trads.label }}</h1>
        <div>{{ trads.subhead.interpolate({ width: data.width, height: data.height }) }}</div>
        <section>
            <Spinner v-if="loading" :style="{ position: 'absolute' }"></Spinner>

            <ul>
                <li v-for="(item, index) in columnsToGenerate" :key="item.id || index">
                    <select v-if="item.type === 'ladder'" required v-model="item.id">
                        <option v-for="ladder in ladders" :key="ladder.id" :value="ladder.id">{{ $tradFor(ladder.terms.name) }}</option>
                    </select>
                    <NumberInput v-else-if="item.type === 'empty'" v-model="item.value" :autofocus="true"></NumberInput>
                    <select v-else-if="item.type === 'shelf'" required v-model="item.id">
                        <option v-for="shelf in shelves" :key="shelf.id" :value="shelf.id">{{ $tradFor(shelf.terms.name) }}</option>
                    </select>
                    <button v-if="index > 0" @click="deleteAt(index);">delete</button>
                </li>

                <li v-if="canAddLadders || canAddShelves || canAddSpace" class="add">
                    <select v-if="canAddLadders" key="ladder" @input="addLadder">
                        <option :value="undefined">Pick a ladder</option>
                        <option v-for="ladder in ladders" :key="ladder.id" :value="ladder.id">{{ $tradFor(ladder.terms.name) }}</option>
                    </select>
                    <button v-if="canAddSpace" @click="addSpace">Add space</button>
                    <select v-if="canAddShelves" key="shelf" @input="addShelf">
                        <option :value="undefined">Pick a shelf</option>
                        <option v-for="shelf in shelves" :key="shelf.id" :value="shelf.id">{{ $tradFor(shelf.terms.name) }}</option>
                    </select>
                </li>
            </ul>

            <div>
                <label>{{ trads.generatedWidth.interpolate({ width: generatedWidth }) }}</label>
            </div>
            <div>
                <label>{{ trads.generatedHeight.interpolate({ height: generatedHeight }) }}</label>
            </div>
        </section>

        <section>
            <h4>{{ trads.paddingLabel }}</h4>
            <div>
                <label>{{ trads.leftPadding }}</label>
                <NumberInput v-model="padding.left"></NumberInput>
            </div>
            <div>
                <label>{{ trads.rightPadding }}</label>
                <NumberInput :disabled="true" v-model="padding.right"></NumberInput>
            </div>
            <div>
                <label>{{ trads.topPadding }}</label>
                <NumberInput v-model="padding.top"></NumberInput>
            </div>
            <div>
                <label>{{ trads.bottomPadding }}</label>
                <NumberInput :disabled="true" v-model="padding.bottom"></NumberInput>
            </div>
        </section>

        <footer style="float: right; margin-top: 5px">
            <ClientColorButton @click="validate">{{ $trad.common.ok }}</ClientColorButton>
            <ClientColorButton @click="cancel">{{ $trad.common.cancel }}</ClientColorButton>
        </footer>
    </ModalBase>
</template>

<script>
import NumberInput from "../components/number-input";
import ModalBase from "@/components/modal/ModalBase";
import ClientColorButton from "@/components/ui/ClientColorButton";
import TryLoadMixin from "@/mixins/TryLoadMixin";
import componentService, { COMPONENT_TYPE, PLANO_STEP, LADDER_TYPE } from "@/services/component";
import Spinner from "@/components/common/Spinner";
import { parseLadder, parseShelf } from "@/plano/utils/parse-catalog";

function spaceBetweenShelfAnchors(shelf) {
    return shelf.config.width - (shelf.config.width / 2 + shelf.config.leftClip.x) - (shelf.config.width / 2 - shelf.config.rightClip.x);
}

export default {
    mixins: [TryLoadMixin],
    props: ["zoneId", "brandId", "campaignId", "zones"],
    components: { NumberInput, ModalBase, ClientColorButton, Spinner },
    data: () => ({
        data: undefined,
        visible: false,
        onValidate: undefined,
        onCancel: undefined,
        ladders: [],
        shelves: [],
        /** @type {Array<{ type: 'ladder' | 'shelf', id: number } | { type: 'empty', value: number } >} */
        columnsToGenerate: [],
        padding: {
            bottom: 0,
            top: 0,
            left: 0,
            right: 0
        }
    }),
    created() {
        window.generateLadders = this.onEdit;
    },
    computed: {
        trads() {
            return this.$trad.plano.editor.generateLadders;
        },
        canAddLadders() {
            return (
                this.columnsToGenerate.length === 0 ||
                this.columnsToGenerate[this.columnsToGenerate.length - 1].type === "empty" ||
                this.columnsToGenerate[this.columnsToGenerate.length - 1].type === "shelf"
            );
        },
        canAddSpace() {
            return this.columnsToGenerate.length > 0 && this.columnsToGenerate[this.columnsToGenerate.length - 1].type === "ladder";
        },
        canAddShelves() {
            return this.columnsToGenerate.length > 0 && this.columnsToGenerate[this.columnsToGenerate.length - 1].type === "ladder";
        },
        generatedHeight() {
            return this.columnsToGenerate.reduce((acc, value) => {
                switch (value.type) {
                    case "ladder":
                        const ladder = this.ladders.find(s => s.id === value.id);
                        return Math.max(acc, ladder && ladder.config.height);
                    default:
                        return acc;
                }
            }, 0);
        },
        generatedWidth() {
            return this.columnsToGenerate.reduce((acc, value) => {
                switch (value.type) {
                    case "empty":
                        return acc + (value.value || 0);
                    case "shelf":
                        const shelf = this.shelves.find(s => s.id === value.id);
                        if (!shelf) return acc;
                        const width = shelf.config.width;
                        return acc + spaceBetweenShelfAnchors(shelf);
                    case "ladder":
                        const ladder = this.ladders.find(s => s.id === value.id);
                        if (!ladder || ladder.config.type === LADDER_TYPE.simple) return acc;
                        // the spacing between points is width / 4
                        return acc + ladder.config.width / 4;

                    default:
                        return acc;
                }
            }, 0);
        }
    },
    watch: {
        generatedHeight(value) {
            this.padding.top = this.padding.bottom = (this.data.height - value) / 2;
        },
        generatedWidth(value) {
            this.padding.left = this.padding.right = (this.data.width - value) / 2;
        }
    },
    methods: {
        deleteAt(index) {
            this.columnsToGenerate = [...this.columnsToGenerate];
            this.columnsToGenerate.splice(index, 1);
        },
        addSpace() {
            this.columnsToGenerate = [
                ...this.columnsToGenerate,
                {
                    type: "empty",
                    value: 0
                }
            ];
        },
        addShelf(e) {
            this.columnsToGenerate = [
                ...this.columnsToGenerate,
                {
                    type: "shelf",
                    id: Number.parseInt(e.target.value, 10)
                }
            ];
        },
        addLadder(e) {
            this.columnsToGenerate = [
                ...this.columnsToGenerate,
                {
                    type: "ladder",
                    id: Number.parseInt(e.target.value, 10)
                }
            ];
        },
        async fetchLaddersAndShelves() {
            const [ladders, shelves] = await this.tryLoad(() =>
                Promise.all([
                    componentService.byType(COMPONENT_TYPE.ladder, {
                        brandId: this.brandId,
                        campaignId: this.campaignId,
                        zoneId: this.zoneId,
                        zones: this.zones,
                        step: PLANO_STEP.template
                    }),
                    componentService.byType(COMPONENT_TYPE.shelf, {
                        brandId: this.brandId,
                        campaignId: this.campaignId,
                        zoneId: this.zoneId,
                        zones: this.zones,
                        step: PLANO_STEP.template
                    })
                ])
            );
            this.ladders = ladders.filter(s => s && s.config);
            this.shelves = shelves.filter(s => s && s.config);
        },
        validate() {
            Context.memento.open();
            let x = this.padding.left;
            const y = this.padding.top;

            for (const item of this.columnsToGenerate) {
                switch (item.type) {
                    case "empty":
                        x += item.value;
                    case "ladder":
                        const ladder = this.ladders.find(s => s.id === item.id);
                        if (!ladder) break;
                        const ladderElement = parseLadder(ladder)();
                        this.data.element.add(ladderElement);

                        const localX = (-this.data.width / 2 + x) * Context.rescaleFactor;
                        const localY = (-this.data.height / 2 + y) * Context.rescaleFactor;

                        ladderElement.position(localX, localY);
                        if (ladder.type === LADDER_TYPE.double) x += ladder.width / 4;
                    case "shelf":
                        const shelf = this.shelves.find(s => s.id === item.id);
                        if (!shelf) break;
                        x += spaceBetweenShelfAnchors(shelf);
                }
            }

            this.cancel();
        },
        onEdit(data, onValidate, onCancel) {
            this.columnsToGenerate = [];
            this.data = data;
            this.ladder = [];
            this.shelves = [];
            this.visible = true;
            this.onValidate = onValidate;
            this.onCancel = onCancel;

            this.fetchLaddersAndShelves();
        },
        cancel() {
            this.data = undefined;
            this.onCancel();
            this.visible = false;
        }
    }
};
</script>

<style lang="scss" scoped>
section {
    margin: 10px 0px;
}
</style>



// WEBPACK FOOTER //
// generate_ladders.vue?ab5a1626